import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const RatesPage = () => {
  const [activeTab, setActiveTab] = useState<'insurance' | 'payment'>('insurance');

  const insurancePlans = [
    {
      name: 'Optum',
      description: 'Comprehensive coverage for psychiatric services and medication management',
      icon: 'ri-shield-check-line'
    },
    {
      name: 'Aetna',
      description: 'Full mental health benefits including telepsychiatry services',
      icon: 'ri-shield-check-line'
    },
    {
      name: 'Cigna',
      description: 'Behavioral health coverage with flexible appointment options',
      icon: 'ri-shield-check-line'
    },
    {
      name: 'Medicaid',
      description: 'State-funded healthcare for eligible Colorado residents',
      icon: 'ri-shield-check-line'
    },
    {
      name: 'Medicare',
      description: 'Federal health insurance for seniors and eligible individuals',
      icon: 'ri-shield-check-line'
    },
    {
      name: 'Colorado Access',
      description: 'Regional health plan serving Colorado communities',
      icon: 'ri-shield-check-line'
    }
  ];

  const paymentOptions = [
    {
      title: 'Private Pay',
      description: 'We offer competitive private pay rates for patients who prefer to pay out-of-pocket. Contact our office for current pricing and payment arrangements.',
      icon: 'ri-money-dollar-circle-line'
    },
    {
      title: 'Self-Pay Options',
      description: 'We offer competitive self-pay rates for patients without insurance coverage. Contact our office for current pricing.',
      icon: 'ri-wallet-3-line'
    },
    {
      title: 'Super-Bills',
      description: 'We provide detailed super-bills for all services rendered. These itemized statements can be submitted to your insurance company for potential out-of-network reimbursement.',
      icon: 'ri-file-list-3-line'
    },
    {
      title: 'Reimbursement Support',
      description: 'Our billing team assists patients in understanding their benefits and navigating the reimbursement process with out-of-network providers.',
      icon: 'ri-customer-service-2-line'
    },
    {
      title: 'Payment Plans',
      description: 'Flexible payment arrangements available for qualifying patients. Speak with our financial coordinator to discuss options.',
      icon: 'ri-calendar-check-line'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Banner */}
      <section className="relative h-[40vh] flex items-center justify-center bg-gradient-to-r from-primary to-primary-dark">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLXdpZHRoPSIyIi8+PC9nPjwvc3ZnPg==')]"></div>
        </div>
        <div className="relative z-10 text-center px-6">
          <motion.h1
            className="text-5xl md:text-6xl font-bold text-white mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Insurance & Payment Options
          </motion.h1>
          <motion.p
            className="text-white/80 text-lg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <Link to="/" className="hover:text-white cursor-pointer">Home</Link> <span className="mx-2">/</span> Rates
          </motion.p>
        </div>
      </section>

      {/* Tab Navigation */}
      <section className="container mx-auto px-6 -mt-8 relative z-20">
        <div className="bg-white rounded-xl shadow-lg p-2 inline-flex gap-2">
          <button
            onClick={() => setActiveTab('insurance')}
            className={`px-8 py-3 rounded-lg font-semibold transition-all whitespace-nowrap ${
              activeTab === 'insurance'
                ? 'bg-[rgba(75,162,75)] text-white shadow-md'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <i className="ri-shield-check-line mr-2"></i>
            Insurance Plans
          </button>
          <button
            onClick={() => setActiveTab('payment')}
            className={`px-8 py-3 rounded-lg font-semibold transition-all whitespace-nowrap ${
              activeTab === 'payment'
                ? 'bg-[rgba(75,162,75)] text-white shadow-md'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <i className="ri-wallet-3-line mr-2"></i>
            Payment Options
          </button>
        </div>
      </section>

      {/* Insurance Plans Tab */}
      {activeTab === 'insurance' && (
        <section className="container mx-auto px-6 py-16">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Accepted Insurance Plans</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                We are in-network with the following insurance providers. Please verify your specific coverage with your insurance company.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {insurancePlans.map((plan, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl p-8 shadow-md hover:shadow-xl transition-all border border-gray-100 hover:border-[rgba(75,162,75)]"
                >
                  <div className="w-14 h-14 bg-[rgba(75,162,75,0.1)] rounded-full flex items-center justify-center mb-6">
                    <i className={`${plan.icon} text-2xl text-[rgba(75,162,75)]`}></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{plan.name}</h3>
                  <p className="text-gray-600 leading-relaxed">{plan.description}</p>
                </div>
              ))}
            </div>

            <div className="mt-12 bg-[rgba(75,162,75,0.1)] rounded-xl p-8 border border-[rgba(75,162,75,0.3)]">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[rgba(75,162,75)] rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-information-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Important Insurance Information</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-[rgba(75,162,75)] mt-1"></i>
                      <span>Please bring your insurance card to your first appointment</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-[rgba(75,162,75)] mt-1"></i>
                      <span>Verify your mental health benefits and any copay requirements before your visit</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-[rgba(75,162,75)] mt-1"></i>
                      <span>Some plans may require prior authorization for certain services</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-[rgba(75,162,75)] mt-1"></i>
                      <span>Contact our billing department if you have questions about coverage</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="mt-8 bg-red-50 rounded-xl p-8 border border-red-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-alert-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Cancellation Policy</h3>
                  <p className="text-gray-700 font-semibold">
                    Without a 24-hour cancellation notice, you will be charged a $100 fee. Please contact our office as soon as possible if you need to reschedule your appointment.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Payment Options Tab */}
      {activeTab === 'payment' && (
        <section className="container mx-auto px-6 py-16">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Flexible Payment Solutions</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                We believe financial concerns should never be a barrier to quality mental healthcare. We offer multiple payment options to fit your needs.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {paymentOptions.map((option, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl p-8 shadow-md hover:shadow-xl transition-all border border-gray-100"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-[rgba(75,162,75)] to-[rgba(75,162,75,0.8)] rounded-xl flex items-center justify-center mb-6">
                    <i className={`${option.icon} text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{option.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{option.description}</p>
                </div>
              ))}
            </div>

            <div className="mt-12 bg-gradient-to-r from-[rgba(75,162,75)] to-[rgba(75,162,75,0.9)] rounded-xl p-10 text-white">
              <div className="max-w-3xl mx-auto text-center">
                <h3 className="text-2xl font-bold mb-4">Questions About Billing or Insurance?</h3>
                <p className="text-white/80 mb-8 text-lg">
                  Our dedicated billing team is here to help you understand your coverage and payment options. We're committed to making mental healthcare accessible and affordable.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <a
                    href="tel:+13035551234"
                    className="bg-white text-[rgba(75,162,75)] px-8 py-4 rounded-lg font-semibold hover:bg-gray-50 transition-all shadow-lg whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-phone-line mr-2"></i>
                    Call Billing Department
                  </a>
                  <a
                    href="mailto:billing@solutionshealthcare.com"
                    className="bg-[rgba(75,162,75,0.8)] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[rgba(75,162,75,0.7)] transition-all whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-mail-line mr-2"></i>
                    Email Us
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default RatesPage;
